import {createContext, useCallback, useContext, useMemo, useReducer} from 'react'

import {shallowEqual} from '../../../helpers/util'
import type {CellFocus} from '../navigation'

export type RowMenuShortcutState = {
  /**
   * The cell from which the shortcut was just invoked.
   */
  rowMenuShortcutOrigin?: CellFocus
}

const ActionTypes = {
  SET_ROW_MENU_OPEN: 'rowMenuShortcut.setRowMenuOpen',
} as const

type SetRowMenuOpenAction = {
  type: typeof ActionTypes.SET_ROW_MENU_OPEN
  origin?: CellFocus
}

type Action = SetRowMenuOpenAction

export const RowMenuShortcutContext = createContext<{
  rowMenuState: RowMenuShortcutState
  setRowMenuOpen: (origin?: CellFocus) => void
} | null>(null)

const setRowMenuOpen = (origin?: CellFocus): SetRowMenuOpenAction => {
  return {
    type: ActionTypes.SET_ROW_MENU_OPEN,
    origin,
  }
}

const reducer = (state: RowMenuShortcutState, action: Action): RowMenuShortcutState => {
  switch (action.type) {
    case ActionTypes.SET_ROW_MENU_OPEN: {
      const newState = {...state, rowMenuShortcutOrigin: action.origin}

      if (shallowEqual(state, newState)) {
        return state
      }

      return newState
    }
    default:
      return state
  }
}

const initialState = {rowMenuShortcutOrigin: undefined}

export const RowMenuShortcutProvider: React.FC<{
  children: React.ReactNode
}> = ({children}) => {
  const [rowMenuState, dispatch] = useReducer(reducer, initialState)
  const setRowMenuOpenCallback = useCallback((origin?: CellFocus) => {
    dispatch(setRowMenuOpen(origin))
  }, [])
  const value = useMemo(() => {
    return {
      rowMenuState,
      setRowMenuOpen: setRowMenuOpenCallback,
    }
  }, [rowMenuState, setRowMenuOpenCallback])
  return <RowMenuShortcutContext value={value}>{children}</RowMenuShortcutContext>
}

export const useSetRowMenuOpen = () => {
  const context = useContext(RowMenuShortcutContext)
  if (!context) {
    throw new Error('useSetRowMenuOpen must be used within a RowMenuShortcutProvider')
  }
  return {setRowMenuOpen: context.setRowMenuOpen}
}
